import { RefreshCw } from "lucide-react";

import { useOptionalSession } from "@/auth/SessionContext";
import type {
  QuestionnaireAdoptionDetail,
  QuestionnaireDetailUnreadReason,
} from "@/lib/questionnaireAdoption";
import {
  NO_VALIDITY_SENTENCE,
  itemCountSentence,
  scoringSentence,
  severityBandSentence,
  validityStateLabel,
  validityThresholdSentence,
} from "@/lib/questionnaire";
import { Badge } from "@/ui/Badge";
import { Button } from "@/ui/Button";

/**
 * What the questionnaire this project pins actually is (#369).
 *
 * PROP-DRIVEN, for the reason `AdoptionRow` and `SectionAdoptionBlock` are:
 * every state but one is decided by how a read SETTLED — still reading, paused
 * with the connection gone, paused over an answered failure, expired, denied,
 * unimplemented, refused — and none of those is reachable from a fixture a live
 * lane can create on demand. Driving it from the value
 * `lib/questionnaireAdoption.ts` computes covers all of them, including the ones
 * no healthy service produces.
 *
 * The session is the single exception, read from context rather than passed in,
 * exactly as `QueryErrorState` reads it for the same branch: the sign-in that
 * recovers an expired session is the auth adapter's, and threading an adapter
 * through the row to reach it would put an identity concern in `AdoptionRow`'s
 * props. `useOptionalSession` answers `null` outside a provider, so a surface
 * rendered before identity is resolved offers no control rather than a dead one.
 *
 * EVERY WORD BELOW DESCRIBES THE PINNED VERSION. The version label is printed
 * in the lead-in rather than left implicit, because the row beside it also
 * names the library's head, and a clinician must never have to work out which
 * of the two the counts belong to. Where the two differ, the block says in
 * plain words that the newer version is not what is described here — the pin is
 * immutable, so the library moving on changes nothing on this screen until
 * somebody moves it deliberately.
 *
 * UNREAD IS NOT ABSENT, AND NOR IS ONE UNREAD THE SAME AS ANOTHER. A failed,
 * paused or refused read renders as a failed, paused or refused read, never as
 * an instrument with no bands and no validity screen — and each of those says
 * which read did not answer in its own words, because the governing
 * `## Clarifications` requires pending, paused and error to stay as
 * distinguishable here as the surrounding surface already makes them. Its
 * recovery follows the same rule the rest of the surface follows: the control
 * offered is the one that could actually change the answer — a re-read where a
 * re-read could work, a sign-in where the session is what expired — and a
 * denial or an unimplemented endpoint says so instead of wearing a control that
 * would be refused again (#4, #44).
 */
export function PinnedQuestionnaire({
  name,
  version,
  detail,
  behind,
  onRetry,
}: {
  /** The asset's name, as the shared library reports it. */
  name: string;
  /** The exact version this project pins — the subject of every line here. */
  version: string;
  detail: QuestionnaireAdoptionDetail;
  /** Whether the shared library holds a newer version than this pin. */
  behind: boolean;
  /**
   * Re-reads this pinned version, or absent when no retry could clear it.
   *
   * ABSENT IS A STATE. A refusal, a denial, an unimplemented endpoint and a
   * content-kind disagreement are all answers the service already gave;
   * re-issuing them returns the same answer, and a control whose only outcome
   * is the failure beside it is worse than no control.
   */
  onRetry?: () => void;
}) {
  const session = useOptionalSession();

  if (detail.state === "reading") {
    return (
      <p
        role="status"
        aria-live="polite"
        className="text-xs text-muted-foreground"
      >
        Reading what {name} administers at {version}…
      </p>
    );
  }

  if (detail.state === "unread") {
    return (
      <div className="space-y-2">
        <p className="text-xs text-foreground">
          {UNREAD_DETAIL[detail.reason]}
          {detail.code ? ` (${detail.code})` : ""}
        </p>
        {detail.recovery === "reread" && onRetry ? (
          <Button variant="outline" size="sm" onClick={onRetry}>
            <RefreshCw aria-hidden="true" className="h-4 w-4" />
            {`Read ${name} at ${version} again`}
          </Button>
        ) : null}
        {detail.recovery === "sign_in" && session ? (
          // The recovery `QueryErrorState` offers for the same status, in the
          // row's own compact form. A re-read here would be refused for exactly
          // as long as the session is gone, so this is the control, not a
          // second one beside it.
          <Button
            variant="outline"
            size="sm"
            onClick={() => void session.adapter.signIn()}
          >
            Sign in again
          </Button>
        ) : null}
      </div>
    );
  }

  const { content } = detail;

  return (
    <div className="space-y-1 text-xs text-muted-foreground">
      <p className="text-foreground">
        {`What this project administers, as pinned at ${version}:`}
      </p>
      <p className="flex flex-wrap gap-x-3 gap-y-1">
        <span>{itemCountSentence(content)}</span>
        <span>{scoringSentence(content)}</span>
        <span>{severityBandSentence(content)}</span>
      </p>
      <p className="flex flex-wrap items-center gap-2">
        <span>Validity screen:</span>
        {content.validity ? (
          <>
            <Badge tone="outline">{validityStateLabel(content.validity)}</Badge>
            <span>{validityThresholdSentence(content.validity)}</span>
          </>
        ) : (
          <span>{NO_VALIDITY_SENTENCE}</span>
        )}
      </p>
      {behind ? (
        // The pin is immutable and the library has moved past it. Saying so
        // HERE, beside the counts, is what stops the row's own "v4 available in
        // the library" from being read as a description of the numbers above
        // it — they are the pinned version's, and nothing on this surface
        // describes the newer one.
        <p>
          The shared library holds a newer version; nothing above describes it.
        </p>
      ) : null}
    </div>
  );
}

/**
 * Why the pinned version's content is not on screen — one sentence per cause,
 * in `UNAVAILABLE_DETAIL`'s wording discipline.
 *
 * None of them says the instrument lacks anything. Each says which read did not
 * answer, and each ends where the clinician's own knowledge ends.
 *
 * Seven causes rather than three, because that is what the surrounding surface
 * already tells apart and what the governing `## Clarifications` requires this
 * row to keep telling apart. The wording tracks the shell's own states rather
 * than inventing a second vocabulary for the same conditions: `offline` is
 * `OfflineState`'s sentence, `session_expired` is the expiry `QueryErrorState`
 * describes, and `not_implemented` carries the "nothing to retry" that the
 * absence of a control beside it would otherwise leave unexplained.
 */
const UNREAD_DETAIL = {
  offline:
    "This view could not reach the Editor Service, so what this questionnaire administers is not shown. It loads on its own when the connection returns. This is not a statement that it carries no items, bands or validity screen.",
  read_failed:
    "The pinned version's content could not be read, so what this questionnaire administers is not shown. This is not a statement that it carries no items, bands or validity screen.",
  session_expired:
    "Your session has expired, so this pinned version's content was not read and what this questionnaire administers is not shown. Sign in again to read it.",
  read_denied:
    "Your account is not allowed to read this pinned version's content, so what this questionnaire administers is not shown here. Ask a workspace administrator for access to it.",
  not_implemented:
    "The Editor Service accepts this request but has not implemented it yet, so this pinned version's content was not read. This resolves when the endpoint is deployed — there is nothing to retry.",
  read_refused:
    "The Editor Service refused this pinned version's content, so what this questionnaire administers is not shown here. Reading it again would be refused too.",
  content_kind_mismatch:
    "The shared library reports this asset as a questionnaire, and the pinned version's content is not a questionnaire's. Nothing here reconciles the two.",
} as const satisfies Record<QuestionnaireDetailUnreadReason, string>;
